import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const PAGE_SIZES = [25, 50, 100];

/**
 * ListPagination — pager + rows-per-page for the CRM list views.
 *
 * Two things worth knowing:
 *
 * 1. It renders whenever there are ANY rows, not only when totalPages > 1. The
 *    previous pager hid itself on a single page, which also hid the only control
 *    for raising the page size — so a 40-row result was stuck at 25 with no way
 *    to see the rest without inventing a filter.
 *
 * 2. Changing page size resets to page 1. Staying on page 4 while moving 25 -> 100
 *    would silently jump the user past three quarters of the result set.
 */
export default function ListPagination({
  page,
  totalPages = 0,
  total = 0,
  limit,
  onPageChange,
  onLimitChange,
  className = "",
}) {
  if (!total) return null;

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 ${className}`}>
      <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <label htmlFor="rows-per-page" className="whitespace-nowrap">
          Rows
        </label>
        <select
          id="rows-per-page"
          className="select w-auto py-1 text-[13px]"
          value={limit}
          onChange={(e) => {
            onLimitChange(Number(e.target.value));
            onPageChange(1);
          }}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <span className="whitespace-nowrap">of {total.toLocaleString()}</span>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={!canPrev}
            aria-label="Previous page"
            className="btn btn-ghost h-8 w-8 p-0 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="px-1 text-[13px] text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={!canNext}
            aria-label="Next page"
            className="btn btn-ghost h-8 w-8 p-0 disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
